import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, Star } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicForm() {
  const { formId } = useParams();
  const [f, setF] = useState(null);
  const [vals, setVals] = useState({});
  const [done, setDone] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { axios.get(`${API}/public/form/${formId}`).then(({ data }) => setF(data)).catch(() => setF(false)); }, [formId]);

  const set = (id, v) => setVals((s) => ({ ...s, [id]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/public/form/${formId}/submit`, { values: vals });
      if (data.redirect_url) { window.location.href = data.redirect_url; return; }
      setDone(data.success_message || "Thank you!");
    } catch { setDone("Thank you!"); }
    finally { setSubmitting(false); }
  };

  if (f === null) return <div className="min-h-screen flex items-center justify-center" style={{ background: "#0b1220" }}><Loader2 className="w-8 h-8 animate-spin text-white" /></div>;
  if (f === false) return <div className="min-h-screen flex items-center justify-center text-white/60" style={{ background: "#0b1220" }}>Form not found.</div>;

  const color = f.brand?.color || "#6366F1";

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(140deg,#0b1220,#111827)" }}>
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl" data-testid="public-form-card">
        {done ? (
          <div className="text-center py-6"><CheckCircle2 className="w-12 h-12 mx-auto mb-4" style={{ color }} /><p className="text-lg font-semibold text-slate-900">{done}</p></div>
        ) : (
          <>
            <div className="mb-6"><div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold" style={{ background: color }}>{f.brand?.name?.[0] || "M"}</div>
              <h1 className="text-xl font-bold text-slate-900 mt-4">{f.name}</h1></div>
            <form onSubmit={submit} className="space-y-4">
              {f.fields.map((fld) => (
                <div key={fld.id} className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-700">{fld.label}{fld.required && <span className="text-rose-500"> *</span>}</label>
                  {fld.type === "textarea" ? (
                    <textarea required={fld.required} rows={3} onChange={(e) => set(fld.id, e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-400 resize-none" data-testid={`field-${fld.id}`} />
                  ) : fld.type === "select" ? (
                    <select required={fld.required} onChange={(e) => set(fld.id, e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm text-slate-900 outline-none" data-testid={`field-${fld.id}`}>
                      <option value="">Select…</option>{(fld.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : fld.type === "rating" ? (
                    <div className="flex gap-1">{[1, 2, 3, 4, 5].map((n) => (
                      <button type="button" key={n} onClick={() => set(fld.id, n)} data-testid={`field-${fld.id}-${n}`}><Star className={`w-7 h-7 ${n <= (vals[fld.id] || 0) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} /></button>))}</div>
                  ) : (
                    <input required={fld.required} type={fld.type === "email" ? "email" : fld.type === "phone" ? "tel" : fld.type === "number" ? "number" : "text"} onChange={(e) => set(fld.id, e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-400" data-testid={`field-${fld.id}`} />
                  )}
                </div>
              ))}
              <button type="submit" disabled={submitting} className="w-full rounded-lg py-3 font-semibold text-white flex items-center justify-center gap-2" style={{ background: color }} data-testid="submit-form-button">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : f.submit_text}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
